import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lightbulb,
  CheckCircle,
  ChevronRight,
  Sparkles,
  Layers,
  AlertTriangle,
  Cpu
} from 'lucide-react';
import innovationIdeasDataRaw from '../../data/innovationIdeas.json';
import type { InnovationIdeasData } from '../../types/innovationIdeas';

interface TRIZPrinciple {
  id: number;
  name: string;
  description: string;
  contradiction: string;
  application: string;
  examples: string[];
  innovationLevel: number;
}

interface GeneratedIdea {
  id: string;
  title: string;
  description: string;
  trizPrinciples: number[];
  category: string;
  innovationScore: number;
  feasibilityScore: number;
  impactScore: number;
  generatedBy: string;
  reasoning: string;
  implementationSteps: string[];
}

interface ContradictionMatrix {
  improving: string;
  worsening: string;
  principles: number[];
  description: string;
  solutions: string[];
}

interface TrizIntegrationPhaseProps {
  companyData: any;
  researchResults: any;
  onComplete: (results: any) => void;
}

const innovationIdeasData = innovationIdeasDataRaw as InnovationIdeasData;

const TrizIntegrationPhase: React.FC<TrizIntegrationPhaseProps> = ({
  onComplete
}) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingLogs, setProcessingLogs] = useState<string[]>([]);
  const [generatedIdeas, setGeneratedIdeas] = useState<GeneratedIdea[]>([]);
  const [selectedIdea, setSelectedIdea] = useState<GeneratedIdea | null>(null);
  const [stageProgress, setStageProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [streamingResults, setStreamingResults] = useState<any>({});

  const trizPrinciples: TRIZPrinciple[] = [
    {
      id: 1,
      name: 'Segmentation',
      description: 'Divide an object into independent parts',
      contradiction: 'Complexity vs Simplicity',
      application: 'Modular PII detection engines for different data types',
      examples: ['Separate text, image, and audio PII detectors', 'Modular compliance modules', 'Independent classification pipelines'],
      innovationLevel: 7.5
    },
    {
      id: 2,
      name: 'Taking Out',
      description: 'Separate interfering parts or properties',
      contradiction: 'Performance vs Features',
      application: 'Isolate sensitive data processing from main application flow',
      examples: ['Dedicated privacy processing layer', 'Separate compliance audit trail', 'Isolated sensitive data storage'],
      innovationLevel: 8.2
    },
    {
      id: 15,
      name: 'Dynamics',
      description: 'Make objects or systems adaptive',
      contradiction: 'Stability vs Adaptability',
      application: 'Adaptive PII classification based on context and regulations',
      examples: ['Context-aware privacy scoring', 'Dynamic compliance rules', 'Adaptive sensitivity thresholds'],
      innovationLevel: 9.1
    },
    {
      id: 17,
      name: 'Another Dimension',
      description: 'Move to multi-dimensional space',
      contradiction: 'Single vs Multiple perspectives',
      application: 'Multi-dimensional privacy analysis (time, geography, context)',
      examples: ['Temporal privacy patterns', 'Geographic compliance mapping', 'Contextual sensitivity analysis'],
      innovationLevel: 8.7
    },
    {
      id: 35,
      name: 'Parameter Changes',
      description: 'Change physical or chemical states',
      contradiction: 'Fixed vs Variable parameters',
      application: 'Transform data representation for better privacy detection',
      examples: ['Encrypted computation on sensitive data', 'Privacy-preserving data transformations', 'Homomorphic encryption processing'],
      innovationLevel: 9.3
    },
    {
      id: 40,
      name: 'Composite Materials',
      description: 'Use composite structures',
      contradiction: 'Uniformity vs Diversity',
      application: 'Hybrid privacy protection combining multiple techniques',
      examples: ['ML + rule-based detection', 'Multi-vendor privacy stack', 'Layered privacy architecture'],
      innovationLevel: 8.4
    }
  ];

  const stages = [
    { name: 'TRIZ Principles Application', icon: Layers, duration: 5500, progress: 20 },
    { name: 'Contradiction Analysis', icon: AlertTriangle, duration: 5500, progress: 40 },
    { name: 'Mechanized Thinking Process', icon: Cpu, duration: 6000, progress: 60 },
    { name: 'Seed Ideas Generation', icon: Lightbulb, duration: 6000, progress: 80 },
    { name: 'LLM Enhancement', icon: Sparkles, duration: 5000, progress: 100 }
  ];

  const generateContradictions = () => {
    return [
      {
        improving: 'Detection Accuracy',
        worsening: 'Processing Speed',
        principles: [1, 15, 35],
        description: 'Higher accuracy models require more computational resources',
        solutions: [
          'Implement hierarchical detection (fast screening + detailed analysis)',
          'Use adaptive processing based on data sensitivity',
          'Deploy edge computing for local processing'
        ]
      },
      {
        improving: 'Regulatory Compliance',
        worsening: 'System Flexibility',
        principles: [2, 17, 40],
        description: 'Strict compliance rules limit system adaptability',
        solutions: [
          'Modular compliance framework with pluggable rules',
          'Multi-dimensional compliance mapping',
          'Hybrid governance with automated and manual processes'
        ]
      },
      {
        improving: 'Data Privacy',
        worsening: 'Feature Richness',
        principles: [15, 35],
        description: 'Enhanced privacy protection limits available features',
        solutions: [
          'Privacy-preserving feature engineering',
          'Differential privacy techniques',
          'Federated learning for model improvement'
        ]
      }
    ];
  };

  const loadIdeasFromJson = () => {
    // Convert JSON ideas to GeneratedIdea format
    const convertedIdeas: GeneratedIdea[] = innovationIdeasData.ideas.map((idea) => ({
      id: `idea-${idea.id}`,
      title: idea.title,
      description: idea.description,
      trizPrinciples: idea.trizPrinciples,
      category: idea.category,
      innovationScore: idea.score,
      feasibilityScore: idea.difficulty === 'Low' ? 9.0 : idea.difficulty === 'Medium' ? 7.5 : idea.difficulty === 'High' ? 6.0 : 4.5,
      impactScore: idea.impact === 'Revolutionary' ? 9.5 : idea.impact === 'High' ? 8.0 : idea.impact === 'Medium' ? 6.5 : 5.0,
      generatedBy: 'TRIZ-Enhanced JSON Database',
      reasoning: `Advanced innovation leveraging TRIZ principles ${idea.trizPrinciples.join(', ')} for ${idea.category.toLowerCase()} enhancement`,
      implementationSteps: [
        'Conduct detailed feasibility analysis',
        'Design comprehensive technical architecture',
        'Implement core algorithms and systems',
        'Validate solution through testing',
        'Deploy and monitor performance metrics'
      ]
    }));

    return convertedIdeas;
  };

  const startProgressiveTrizProcessing = async () => {
    setIsProcessing(true);
    setStageProgress(0);
    setProcessingLogs(['🧠 Initializing TRIZ Innovation Engine...']);
    setStreamingResults({});
    
    const multiplier = 1.0;
    
    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      setCurrentStage(i);
      
      // Add stage start log
      setProcessingLogs(prev => [...prev, '', `🔧 ${stage.name} - STARTING`]);
      
      // Simulate progressive stage processing
      await new Promise(resolve => {
        let stageProgress = i > 0 ? stages[i-1].progress : 0;
        const increment = (stage.progress - stageProgress) / 10;
        const adjustedDuration = stage.duration * multiplier;
        
        const updateProgress = () => {
          stageProgress += increment;
          setStageProgress(Math.min(stageProgress, stage.progress));
          
          // Stream partial results
          if (stageProgress >= stage.progress * 0.6) {
            setStreamingResults((prev: any) => ({
              ...prev,
              [stage.name]: getStageResults(stage.name, stageProgress / stage.progress)
            }));
          }
          
          if (stageProgress >= stage.progress) {
            // Add stage completion logs
            const completionLogs = getStageCompletionLogs(stage.name);
            setProcessingLogs(prev => [...prev, ...completionLogs]);
            resolve(undefined);
          } else {
            setTimeout(updateProgress, adjustedDuration / 10);
          }
        };
        updateProgress();
      });
    }
    
    // Final completion
    setProcessingLogs(prev => [...prev, '', '🎉 TRIZ Integration completed successfully!']);
    const ideas = loadIdeasFromJson();
    setGeneratedIdeas(ideas);
    setIsProcessing(false);
    setIsCompleted(true);
  };

  // Generate stage-specific results for streaming
  const getStageResults = (stageName: string, completion: number) => {
    switch (stageName) {
      case 'TRIZ Principles Application':
        return {
          principlesAnalyzed: Math.round(6 * completion),
          contradictionsMapped: Math.round(8 * completion),
          transformationPatterns: Math.round(12 * completion)
        };
      case 'Contradiction Analysis':
        return {
          contradictionsIdentified: Math.round(3 * completion),
          resolutionStrategies: Math.round(15 * completion)
        };
      case 'Mechanized Thinking Process':
        return {
          conceptsGenerated: Math.round(2847 * completion),
          transformationRules: Math.round(40 * completion)
        };
      case 'Seed Ideas Generation':
        return {
          refinedConcepts: Math.round(innovationIdeasData.metadata.totalIdeas * completion),
          scoringDimensions: Math.round(5 * completion)
        };
      case 'LLM Enhancement':
        return {
          enhancedIdeas: Math.round(innovationIdeasData.metadata.totalIdeas * completion),
          implementationSteps: Math.round(5 * completion)
        };
      default:
        return { progress: completion };
    }
  };

  // Get stage completion logs
  const getStageCompletionLogs = (stageName: string) => {
    switch (stageName) {
      case 'TRIZ Principles Application':
        return [
          'Analyzed 6 core TRIZ principles for PII detection domain',
          'Mapped contradictions to innovation opportunities',
          'Identified 12 applicable transformation patterns'
        ];
      case 'Contradiction Analysis':
        return [
          'Identified 3 primary system contradictions',
          'Mapped to TRIZ resolution matrix',
          'Generated 15 resolution strategies'
        ];
      case 'Mechanized Thinking Process':
        return [
          'Applied systematic idea generation algorithms',
          'Generated 2,847 initial solution concepts',
          'Applied TRIZ transformation rules'
        ];
      case 'Seed Ideas Generation':
        return [
          `Created ${innovationIdeasData.metadata.totalIdeas} refined solution concepts`,
          'Scored innovation potential across 5 dimensions',
          'Ranked ideas by feasibility and impact'
        ];
      case 'LLM Enhancement':
        return [
          'Enhanced ideas with AI processing',
          'Added detailed implementation steps',
          'Optimized for business value alignment'
        ];
      default:
        return ['Stage completed'];
    }
  };

  const handleComplete = () => {
    onComplete({
      contradictionMatrix: generateContradictions(),
      generatedIdeas: generatedIdeas,
      summary: {
        totalIdeas: generatedIdeas.length,
        avgInnovationScore: generatedIdeas.reduce((sum, idea) => sum + idea.innovationScore, 0) / generatedIdeas.length,
        topCategories: ['Core Technology', 'Security Innovation', 'User Experience', 'Network Architecture'],
        recommendedPrinciples: trizPrinciples.slice(0, 3).map(p => p.name),
        innovationBreakthroughs: [
          'Homomorphic PII processing pipeline',
          'Quantum-resistant privacy protection',
          'Real-time compliance automation',
          'Federated privacy learning network'
        ]
      }
    });
  };

  const startTrizIntegration = () => {
    startProgressiveTrizProcessing();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 sqsft-gradient-primary rounded-2xl flex items-center justify-center">
            <Lightbulb className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-4xl lg:text-5xl sqsft-heading-1 mb-2">TRIZ Integration & Innovation</h2>
            <p className="text-lg sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>Apply TRIZ principles for systematic innovation</p>
          </div>
        </div>
        
        {!isProcessing && !isCompleted && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={startTrizIntegration}
            className="sqsft-btn-primary"
          >
            Start TRIZ Integration
          </motion.button>
        )}
        
        {isCompleted && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleComplete}
            className="sqsft-btn-primary"
          >
            Complete Phase
          </motion.button>
        )}
      </div>

      {/* Processing Status */}
      {isProcessing && (
        <div className="space-y-6">
          <div className="sqsft-card backdrop-blur-xl rounded-2xl p-6 border border-slate-200/50 shadow-lg" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="sqsft-body font-medium">
                  Stage {currentStage + 1}/5: {stages[currentStage]?.name}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <span className="font-bold text-lg" style={{ color: 'rgb(var(--color-deep-green))' }}>{stageProgress.toFixed(0)}%</span>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <motion.div
                className="h-3 rounded-full"
                style={{ backgroundColor: 'rgb(var(--color-sage-green))' }}
                initial={{ width: 0 }}
                animate={{ width: `${stageProgress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
          
          {/* Streaming Results */}
          {Object.keys(streamingResults).length > 0 && (
            <div className="sqsft-card backdrop-blur-xl rounded-2xl p-6 border border-slate-200/50 shadow-lg" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}>
              <h4 className="sqsft-heading-4 mb-4">Live Results Stream</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(streamingResults).map(([stage, data]: [string, any]) => (
                  <div key={stage} className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-700 mb-2">{stage}</div>
                    <div className="space-y-1">
                      {Object.entries(data).map(([key, value]: [string, any]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-gray-600">{key.replace(/([A-Z])/g, ' $1')}</span>
                          <span className="font-medium">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Completion Status */}
      {isCompleted && !isProcessing && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-emerald-600" />
            <div>
              <h3 className="sqsft-heading-3" style={{ color: 'rgb(var(--color-deep-green))' }}>TRIZ Integration Complete!</h3>
              <p className="sqsft-caption" style={{ color: 'rgb(var(--color-deep-green))' }}>
                Generated {generatedIdeas.length} innovative solutions. Ready to proceed to next phase.
              </p>
            </div>
          </div>
        </div>
      )}

      {!isProcessing && !isCompleted ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            <div className="sqsft-feature-card p-6">
                <h3 className="sqsft-heading-3 mb-4">
                    <span className="sqsft-gradient-text">Live Integration Process</span>
                </h3>
                <div className="rounded-xl p-4 h-96 overflow-y-auto font-mono text-sm" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="w-16 h-16 sqsft-gradient-primary rounded-2xl flex items-center justify-center mb-4">
                            <Cpu className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="sqsft-heading-4 mb-2">TRIZ Engine</h4>
                        <p className="sqsft-body max-w-xs" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                            The live innovation process will be shown here. Click "Start TRIZ Integration" to begin.
                        </p>
                    </div>
                </div>
            </div>
            <div className="sqsft-feature-card p-6">
                <h3 className="sqsft-heading-3 mb-4">
                    <span className="sqsft-gradient-text">Integration Framework</span>
                </h3>
                <div className="h-96 overflow-y-auto pr-2">
                    <div>
                        <h4 className="sqsft-heading-4 mb-3">Core Integration Stages</h4>
                        <div className="space-y-3">
                            {stages.map((stage, index) => (
                                <motion.div
                                    key={stage.name}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="sqsft-feature-card p-4 border hover:border-sage-green transition-all duration-300"
                                >
                                    <div className="flex items-center space-x-4">
                                        <div className="w-12 h-12 sqsft-gradient-primary rounded-2xl flex items-center justify-center">
                                            <stage.icon className="w-5 h-5 text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <h5 className="sqsft-heading-4">{stage.name}</h5>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
      ) : (
      <div className="grid grid-cols-1 gap-6">
        {/* Process Logs */}
        <div className="sqsft-feature-card p-6">
          <h3 className="sqsft-heading-3 mb-4">TRIZ Process Logs</h3>
          <div className="bg-slate-100/80 rounded-xl h-96 overflow-y-auto">
            <div className="p-4 font-mono text-sm">
                {processingLogs.map((log, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`${log.startsWith('✅') ? 'text-emerald-600' : 
                              log.startsWith('🧠') || log.startsWith('🎉') ? 'text-green-600' : 
                              log.startsWith('🔧') ? 'text-green-600' :
                              'text-gray-700'} leading-relaxed`}
                  >
                    {log}
                  </motion.div>
                ))}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Generated Ideas */}
      <AnimatePresence>
        {generatedIdeas.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="sqsft-card backdrop-blur-xl rounded-2xl p-6 border border-slate-200/50 shadow-lg" style={{ backgroundColor: 'rgb(var(--color-bg-white))' }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Sparkles className="w-6 h-6" style={{ color: 'rgb(var(--color-deep-green))' }} />
                <h3 className="sqsft-heading-3">Generated Innovation Ideas</h3>
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                  Showing all {generatedIdeas.length} ideas
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 max-h-[600px] overflow-y-scroll">
              {generatedIdeas.map((idea) => (
                <motion.div
                  key={idea.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white/60 rounded-xl p-4 border border-slate-200/50 hover:border-green-300 transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md"
                  onClick={() => setSelectedIdea(idea)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="text-gray-900 font-semibold text-sm mb-1">{idea.title}</h4>
                      <p className="text-gray-600 text-xs mb-2 line-clamp-2">{idea.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-end">
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Idea Detail Modal */}
      <AnimatePresence>
        {selectedIdea && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setSelectedIdea(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-2xl max-h-[80vh] overflow-y-auto border border-slate-200 shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedIdea.title}</h2>
                  <p className="text-gray-600">{selectedIdea.description}</p>
                </div>
                <button
                  onClick={() => setSelectedIdea(null)}
                  className="text-gray-500 hover:text-gray-900 transition-colors"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-gray-900 font-semibold mb-2">TRIZ Reasoning</h3>
                  <p className="text-gray-700 text-sm">{selectedIdea.reasoning}</p>
                </div>
                
                <div>
                  <h3 className="text-gray-900 font-semibold mb-2">Implementation Steps</h3>
                  <ul className="space-y-2">
                    {selectedIdea.implementationSteps.map((step, index) => (
                      <li key={index} className="flex items-start space-x-2 text-gray-700 text-sm">
                        <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                          {index + 1}
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TrizIntegrationPhase;